import { DOCUMENT, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output } from '@angular/core'
import { InvitationCollectionItem, InvitationCollectionItemSource, InvitationSourceDetailsVariant } from '@app/v2/event/types/invitation-tracker.type'
import { buildInvitationSourceFieldRows, formatInvitationCollectionSourceLabel } from '@app/v2/event/utils/invitation-source.util'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { ImportTargetColumn } from '@app/v2/shared/types/import-target-column.type'

@Component({
  selector: 'app-invitation-source-details',
  standalone: true,
  imports: [NgClass, TruncationTooltipComponent],
  templateUrl: './invitation-source-details.component.html',
  styleUrl: './invitation-source-details.component.scss',
  host: {
    class: 'tw-flex tw-min-h-0'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationSourceDetailsComponent {
  public readonly item = input.required<InvitationCollectionItem>()
  public readonly source = input<InvitationCollectionItemSource | null>(null)
  public readonly loading = input(false)
  public readonly variant = input<InvitationSourceDetailsVariant>('panel')
  public readonly fields = input<ImportTargetColumn[]>([])

  public readonly close = output<void>()

  public readonly skeletonRows = [0, 1, 2, 3]

  public readonly isDrawer = computed(() => this.variant() === 'drawer')

  public readonly sourceLabel = computed(() => formatInvitationCollectionSourceLabel(this._sourceRecord()))

  public readonly personaName = computed(() => this._sourceRecord().source_data?.persona_name ?? '')

  public readonly fieldRows = computed(() => buildInvitationSourceFieldRows(this.fields(), this.source()?.data))

  private readonly _modalService = inject(ModalService)
  private readonly _document = inject(DOCUMENT)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _sourceRecord = computed(() => this.source() ?? this.item())
  private _isClosing = false

  /**
   * Closes the panel on Escape unless another overlay on top should handle it.
   */
  private readonly _onEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this._isClosing || this._isEscapeOwnedByOverlay()) return
    event.stopPropagation()
    this._isClosing = true
    this.close.emit()
  }

  constructor() {
    // Capture phase so the parent wizard's document Escape listener doesn't also close the tracker.
    this._document.addEventListener('keydown', this._onEscape, true)
    this._destroyRef.onDestroy(() => this._document.removeEventListener('keydown', this._onEscape, true))
  }

  /**
   * Whether a modal, CDK overlay, or body-mounted dropdown is open and should receive Escape first.
   */
  private _isEscapeOwnedByOverlay(): boolean {
    return (
      this._modalService.hasOpenModal() ||
      !!this._document.querySelector('.cdk-overlay-backdrop.cdk-overlay-backdrop-showing') ||
      !!this._document.querySelector('app-dropdown-content')
    )
  }
}
